// `_`-prefixed identifiers below are an intentional naming convention for
// module-private infrastructure. `_activeHost` is exported only so the
// $LitElement mixin can wrap render/lifecycle in `_activeHost.run`; the
// underscore signals "not part of the public state API."
/* oxlint-disable no-underscore-dangle */

// Active-host tracker for context-aware state resolution.
//
// Polyfills the TC39 `AsyncContext.Variable` propagation primitive using a
// stack + a one-time `Promise.prototype.then` patch. The stack value carries
// across `await`, `queueMicrotask`, RxJS subscription callbacks, and any
// other code path that ultimately routes through `Promise.then`. This is the
// same capture-and-restore technique zone.js, async-hooks, and the various
// AsyncContext userland polyfills use.
//
// Why we own this:
//   - The TC39 proposal is at stage 2; no official polyfill ships from TC39.
//   - The `async-context@1.0.0` package on npm is unrelated (a 2014 Node
//     callback helper), and the available userland polyfills either depend
//     on zone.js (~50 KB) or are Node-only / hobby quality.
//   - The patch surface is ~30 lines, idempotent, preserves Promise
//     semantics (delegates to the original `then`, so `Symbol.species` and
//     subclassing work unchanged), and decommissions cleanly the day a real
//     polyfill or native AsyncContext lands (drop the file, swap the
//     `_activeHost` export for the standard primitive).
//
// Known limitation — native await on a native Promise: V8's await
// optimization (since 7.x) skips the spec-prescribed
// `Promise.resolve(x).then(continuation)` step, so userland `then`
// patches don't intercept the resumption. Class methods that mutate
// state across an `await` boundary will fall back to the
// module-scoped global, not the active-host's isolated copy. To
// preserve the host across awaits, either keep the mutation in the
// synchronous prelude before the first `await`, or explicitly chain
// with `.then()` (which still routes through the patched method).
// A real fix requires either a build-time async-function transform
// or native AsyncContext.Variable in the runtime.
//
// resolveActiveHost() fallback chain:
//   1. Stack value set by `_activeHost.run(host, fn)` — covers render(),
//      Lit lifecycle methods, and class-method handlers (all wrapped by
//      SchmancyElement's prototype-chain wrap).
//   2. The closest `<schmancy-context>` whose subtree currently has an
//      in-flight DOM event. The element installs capture-phase listeners
//      that publish itself as the event-host while the synchronous
//      handler chain runs; the slot clears via microtask. Covers
//      template-bound inline arrow handlers attached to descendants.
//   3. document.activeElement — keyboard handlers in focus.
//   4. undefined → caller falls back to the module-scoped global instance.

// Singleton across any number of schmancy module copies (source + dist, ESM
// + CJS, hoisted vs. nested node_modules) via the runtime-global symbol
// registry. The first copy to load creates the slot; later copies reuse it.
// Without this, two stacks → mixins push to one, state reads from the other,
// and active-host context is lost across the boundary.
const STACK_KEY = Symbol.for('schmancy.state.activeHost.stack')
type StackSlot = { stack: Array<HTMLElement | undefined> }
const _g = globalThis as { [STACK_KEY]?: StackSlot }
_g[STACK_KEY] ??= { stack: [undefined] }
const _stack = _g[STACK_KEY].stack

// One-time Promise.prototype.then patch at module init. Each .then() call
// captures the stack head at chain time; firing the callback restores that
// captured value for the duration of the callback. The guard property
// prevents double-patching across HMR / duplicate module loads.
declare global {
	interface Promise<T> {
		// Marker symbol so we can detect (and skip) re-patching.
		readonly __schmancyActiveHostPatched?: true
	}
}

// Patching Promise.prototype is the entire mechanism this module exists to
// provide — the lint rules below intentionally do not apply.
/* oxlint-disable no-extend-native, unicorn/no-thenable */
if (typeof Promise !== 'undefined' && !('__schmancyActiveHostPatched' in Promise.prototype)) {
	Object.defineProperty(Promise.prototype, '__schmancyActiveHostPatched', {
		value: true,
		configurable: false,
		enumerable: false,
		writable: false,
	})
	const _origThen = Promise.prototype.then
	// Replace with our wrapper. We delegate to the original `then` via
	// `_origThen.call(this, …)` so Promise subclassing / Symbol.species behave
	// exactly as before — the only added work is the stack push/pop around
	// each user callback.
	Promise.prototype.then = function schmancyThen<T, R1, R2>(
		this: Promise<T>,
		onfulfilled?: ((value: T) => R1 | PromiseLike<R1>) | null,
		onrejected?: ((reason: unknown) => R2 | PromiseLike<R2>) | null,
	): Promise<R1 | R2> {
		const captured = _stack[_stack.length - 1]
		const wrapFulfilled = onfulfilled
			? (v: T): R1 | PromiseLike<R1> => {
					_stack.push(captured)
					try {
						return onfulfilled(v)
					} finally {
						_stack.pop()
					}
				}
			: onfulfilled
		const wrapRejected = onrejected
			? (r: unknown): R2 | PromiseLike<R2> => {
					_stack.push(captured)
					try {
						return onrejected(r)
					} finally {
						_stack.pop()
					}
				}
			: onrejected
		return _origThen.call(this, wrapFulfilled, wrapRejected) as Promise<R1 | R2>
	}
}
/* oxlint-enable no-extend-native, unicorn/no-thenable */

export const _activeHost = {
	/** Run `fn` with `host` as the active host for the duration of `fn` and
	 *  any awaits / microtasks / Promise chains that descend from it. */
	run<R>(host: HTMLElement | undefined, fn: () => R): R {
		_stack.push(host)
		try {
			return fn()
		} finally {
			_stack.pop()
		}
	},
	/** The current active host, or undefined if no `run()` is on the stack. */
	get(): HTMLElement | undefined {
		return _stack[_stack.length - 1]
	},
}

// Tier 2 of the resolveActiveHost() fallback chain. A `<schmancy-context>`
// publishes itself here from a capture-phase event listener attached to
// itself; the slot clears in the next microtask, after the synchronous
// event-handler chain has run. `window.event` is unreliable across both
// engines and time (it can outlive its event — a stale `message` event
// from postMessage can leak BODY for arbitrary later resolution calls), so
// we own this slot rather than read the legacy global.
//
// Singleton across module copies for the same reason as `_stack` above.
const EVENT_HOST_KEY = Symbol.for('schmancy.state.activeHost.eventHost')
type EventHostSlot = { host: HTMLElement | undefined; scheduled: boolean }
const _eventHostSlot: EventHostSlot = ((globalThis as { [EVENT_HOST_KEY]?: EventHostSlot })[EVENT_HOST_KEY] ??= {
	host: undefined,
	scheduled: false,
})

/** Publish `host` as the active event-host. The slot self-clears at the
 *  end of the current microtask checkpoint — long enough for the entire
 *  synchronous event-handler chain to read it, short enough that no
 *  later, unrelated microtask sees a stale value. */
export function _publishEventHost(host: HTMLElement): void {
	_eventHostSlot.host = host
	if (_eventHostSlot.scheduled) return
	_eventHostSlot.scheduled = true
	queueMicrotask(() => {
		_eventHostSlot.scheduled = false
		_eventHostSlot.host = undefined
	})
}

export function resolveActiveHost(): HTMLElement | undefined {
	// 1. Stack (set by _activeHost.run).
	const fromStack = _activeHost.get()
	if (fromStack !== undefined) return fromStack

	// 2. Closest <schmancy-context> with an in-flight event in its subtree.
	if (_eventHostSlot.host !== undefined) return _eventHostSlot.host

	// 3. document.activeElement — keyboard / focus handlers.
	if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
		return document.activeElement
	}

	// 4. undefined — caller falls back to module-scoped.
	return undefined
}

/** Stable context key for a state namespace. `Symbol.for(...)` so the same
 *  namespace string yields the same symbol across module boundaries — the
 *  ContextProvider on `<schmancy-context>` and the ContextRequestEvent
 *  dispatched by a state read must agree on identity. */
export function stateContextKey(namespace: string): symbol {
	return Symbol.for(`schmancy.state:${namespace}`)
}

// DEV-ONLY module-scope computed guard: tracks which namespaces are
// currently provided by a mounted `<schmancy-context>` and whether the call
// stack is inside a `computed()` callback, so a hostless read of a
// context-provided namespace can be flagged (see `_devShouldWarnContextualRead`
// below). All state is gated behind `import.meta.env.DEV` so production
// allocates nothing. Singleton across module copies (source ↔ dist, Module
// Federation) via the runtime-global symbol registry — same pattern as
// `_stack` / `_eventHostSlot`.

const DEV_SLOT_KEY = Symbol.for('schmancy.state.dev.guardSlot')
type DevSlot = {
	provided: Set<string>
	inComputed: boolean
}

// `_devSlot` is `undefined` in production; only non-null in DEV builds.
// Every consumer guards with `if (import.meta.env.DEV)` before access.
const _devSlot: DevSlot | undefined = import.meta.env.DEV
	? ((globalThis as { [DEV_SLOT_KEY]?: DevSlot })[DEV_SLOT_KEY] ??= {
			provided: new Set<string>(),
			inComputed: false,
		})
	: undefined

/** Called by `<schmancy-context>` in `connectedCallback` for each provided state. */
export function _devTrackProvidedNamespace(namespace: string): void {
	if (import.meta.env.DEV) _devSlot!.provided.add(namespace)
}

/** Called by `<schmancy-context>` in `disconnectedCallback` for each provided state.
 *  A namespace is only removed when ALL providers for it have disconnected. */
export function _devUntrackProvidedNamespace(namespace: string): void {
	if (import.meta.env.DEV) _devSlot!.provided.delete(namespace)
}

/**
 * Returns true when the guard should fire: the call stack is inside a
 * `computed()` callback, the namespace is currently provided by a
 * `<schmancy-context>`, AND no host is on the `_activeHost` stack (stack-only
 * tier-1 check). DEV-only.
 *
 * The third condition (`_activeHost.get() === undefined`) is the discriminator
 * that makes this guard fire only on genuine residual misuse — a `computed.get()`
 * called outside any render/handler frame (e.g. a native-await continuation)
 * that reads a context-provided namespace and falls back to the global
 * singleton. It stays silent on correct per-context reads inside a render frame,
 * where the scope-aware `computed` resolves to the right isolated copy.
 *
 * `resolveActiveHost() === undefined` is NOT used here: it is near-dead
 * because tier-3 returns `document.activeElement` (≈always non-null in a
 * browser), so it would never fire on genuine misuse.
 */
export function _devShouldWarnContextualRead(namespace: string): boolean {
	if (!import.meta.env.DEV) return false
	return _devSlot!.inComputed && _devSlot!.provided.has(namespace) && _activeHost.get() === undefined
}

/** Wrap a `computed` callback so reads inside it are flagged if they
 *  touch a context-provided namespace. */
export function _devWrapComputedCallback<T>(fn: () => T): () => T {
	if (!import.meta.env.DEV) return fn
	return (): T => {
		const prev = _devSlot!.inComputed
		_devSlot!.inComputed = true
		try {
			return fn()
		} finally {
			_devSlot!.inComputed = prev
		}
	}
}
